import React from 'react'

import { ScreenReaderOnly } from '@acorns/web-components'
import { compose, withHandlers, withState } from 'recompose'
import styled from 'styled-components'

import MagnifyingGlassSVG from 'assets/icons/magnifying-glass.svg'
import { TrackClickProps } from 'components/trackers'
import { style } from 'theme'
import { SEARCH_BOX_IMG, SEARCH_TERM_INPUT } from 'utils/testing/testIDs'

const SEARCH_INPUT_ID = 'search-input'

type Props = {
  placeholder?: string
  onSearch: (searchTerm: string) => void
}

type StateProps = {
  searchTerm: string
  setSearchTerm: () => void
  heroSubtitle: string
}

type HandlerProps = {
  handleClear: () => void
  handleChange: () => void
  handleSearch: () => void
  handleKeyPress: () => void
}

type InnerProps = Props & HandlerProps & StateProps & TrackClickProps

const Container = styled.div`
  position: relative;
  background-color: white;
  display: flex;
  padding: 0 20px 40px 20px;
  width: 100%;
  z-index: 1;
`

const SearchInput = styled.input`
  border: none;
  flex: 1;
  color: ${style((theme) => theme.colors.slate)};
  line-height: 30px;
  font-size: 14px;
  font-weight: 400;
  display: flex;
  height: 44px;
  background-color: ${style((theme) => theme.colors.ivory)};
  &&& {
    appearance: none; /* to remove iOS default input field borders */
    border-radius: 0 ${style((theme) => theme.styles.mediumBorderRadius)}
      ${style((theme) => theme.styles.mediumBorderRadius)} 0;
  }
`

const SearchImg = styled.img`
  width: 40px;
  height: 44px;
  padding: 0 10px 0 14px;
  background-color: ${style((theme) => theme.colors.ivory)};
  border-radius: ${style((theme) => theme.styles.mediumBorderRadius)} 0 0
    ${style((theme) => theme.styles.mediumBorderRadius)};
`

const enhance = compose<InnerProps, Props>(
  withState('searchTerm', 'setSearchTerm', ''),
  withHandlers({
    handleClear:
      ({ setSearchTerm }) =>
      () => {
        setSearchTerm('')
      },
    handleChange:
      ({ setSearchTerm, onSearch }) =>
      (e) => {
        // Default ascii chars only. This is to prevent users from entering in emojis.
        // eslint-disable-next-line no-control-regex
        setSearchTerm(e.target.value.replace(/[^\x00-\x7F]/g, ''))
        return e.target.value && onSearch({ searchTerm: e.target.value })
      },
  }),
)

export default enhance((props: InnerProps) => (
  <Container>
    <ScreenReaderOnly>
      <label htmlFor={SEARCH_INPUT_ID}>{props.heroSubtitle}</label>
    </ScreenReaderOnly>
    <SearchImg
      id={SEARCH_INPUT_ID}
      src={MagnifyingGlassSVG}
      alt=""
      data-testid={SEARCH_BOX_IMG}
    />
    <SearchInput
      value={props.searchTerm}
      placeholder={props.placeholder || 'Search'}
      onChange={props.handleChange}
      onBlur={props.handleChange}
      autoFocus={true}
      type="search"
      tabindex="-1"
      data-testid={SEARCH_TERM_INPUT}
    />
  </Container>
))
